import { MAX_PREVIEW_SIZE, VIEWABLE_MIME_TYPES } from '@/constants'

// Record fields may be plain values or `{ value }`-wrapped depending on context.
const fieldVal = (f) => f?.value ?? f

// --- Mime classification: the single source of truth shared by the listing
// (getFileFlags) and the viewer (previewKind/previewSandbox), so the two can
// never disagree about how a type previews. ---

// Media rendered via a streamed `<… :src>` element — no in-memory cost.
const isStreamableMime = (mime = '') =>
  mime.startsWith('image/') ||
  mime.startsWith('video/') ||
  mime.startsWith('audio/')

// Browser-renderable content type (image, pdf, text, json, xml, …).
const isViewableMime = (mime = '') =>
  VIEWABLE_MIME_TYPES.some((t) => mime.startsWith(t))

// Documents the browser renders natively from their URL (in an iframe), so they
// never enter app memory: HTML/XHTML pages, PDF (its reader), XML (its tree).
const DIRECT_URL_DOC_TYPES = [
  'text/html',
  'application/xhtml+xml',
  'application/pdf',
  'text/xml',
  'application/xml',
]
const isDirectUrlDoc = (mime = '') =>
  DIRECT_URL_DOC_TYPES.some((t) => mime.startsWith(t))

// No in-memory cost to preview — streamed media or a browser-native doc. These
// are exempt from the size cap; everything else is read into memory.
const rendersWithoutBuffering = (mime = '') =>
  isStreamableMime(mime) || isDirectUrlDoc(mime)

// How a content type previews — the viewer routes on exactly this:
//   image|video|audio → streamed `<… :src>`
//   direct            → pdf/xml/html rendered from the URL in an `<iframe>`
//   text              → fetched into the styled text sheet
//   none              → not previewable
export function previewKind(mime = '') {
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (isDirectUrlDoc(mime)) return 'direct'
  if (isViewableMime(mime)) return 'text'
  return 'none'
}

// Sandbox attribute for the direct-URL iframe, by type: PDFs get none (Chrome's
// native viewer blanks when sandboxed); HTML pages need scripts. The HTML file
// is served from the signed-storage origin (cross-origin to the app), so
// allow-same-origin gives it *that* origin's storage — it still can't reach the
// app's cookies/storage, and being cross-origin it can't self-remove the
// sandbox. Without the flag the doc gets an opaque origin and any localStorage
// access throws. XML and the text CORS fallback stay locked down (no scripts).
export function previewSandbox(mime = '') {
  if (mime.startsWith('application/pdf')) return undefined
  if (mime.startsWith('text/html') || mime.startsWith('application/xhtml+xml'))
    return 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin'
  return ''
}

// A known byte count over the in-memory preview cap. Shared by the listing and
// the viewer so the threshold lives in exactly one place.
export const exceedsPreviewCap = (bytes) => {
  const n = bytes == null || bytes === '' ? NaN : Number(bytes)
  return Number.isFinite(n) && n > MAX_PREVIEW_SIZE
}

/**
 * File capability flags for a record:
 * - `downloadable`: has a `download_url`, isn't a directory, and isn't offline
 *   (a present status must be ONLINE; status-less records are allowed).
 * - `viewable`: downloadable and a browser-renderable content type. Streamed
 *   media and browser-native docs (PDF/XML/HTML) are uncapped; only the
 *   in-memory text-sheet types must be within MAX_PREVIEW_SIZE.
 * - `contentType`: the type of the served bytes, used as the preview hint.
 * - `filesize`: byte size (number) or null when unknown.
 *
 * Viewability keys on `contenttype` (the actual served type), not `mimetype`:
 * a gzipped text file is `mimetype: text/plain` but `contenttype: application/gzip`,
 * which the browser can't preview, so it must fall through to download.
 */
export function getFileFlags(record) {
  const contentType =
    fieldVal(record.contenttype) || fieldVal(record.mimetype) || ''
  const downloadUrl = fieldVal(record.download_url)
  // Number(null)/Number('') are 0 — treat empty/missing as unknown (null), not
  // a 0-byte file, so unknown-size files aren't mistaken for empty.
  const rawSize = fieldVal(record.filesize)
  const sizeNum = rawSize == null || rawSize === '' ? NaN : Number(rawSize)
  const filesize = Number.isFinite(sizeNum) ? sizeNum : null
  const status =
    record.status?.value?.value ?? record.status?.value ?? record.status

  // Needs a download_url. Hide when a status is present and not ONLINE, and
  // hide directories (status-less records are treated as available).
  const isOnline = status == null || status === 'ONLINE'
  const isDirectory = contentType === 'application/x-directory'
  const downloadable = !!downloadUrl && isOnline && !isDirectory
  if (!downloadable)
    return { downloadable: false, viewable: false, contentType, filesize }

  const mimeViewable = isViewableMime(contentType)
  // Only the in-memory (text-sheet) types are size-capped; media and
  // browser-native docs render without buffering, so they're exempt.
  const sizeOk =
    rendersWithoutBuffering(contentType) ||
    (filesize != null && !exceedsPreviewCap(filesize))
  const viewable = mimeViewable && sizeOk

  return { downloadable, viewable, contentType, filesize }
}
